import { useState } from "react";
import {
  useQueuePreview,
  useRegisterCounts,
  useRegisterList,
  type Queue,
  type QueueCounts,
  type QuotationRow,
  type Stage,
} from "../../api/quotations";
import { Segmented } from "../../components/ui";
import { useDebounced } from "../../hooks/useDebounced";
import DraftRows from "./DraftRows";
import JobRow from "./JobRow";
import { queueLabel, sectionsFor, type QueueSection } from "./queues";

type View = "action" | "all" | "done";
type Order = "newest" | "oldest";

interface Props {
  selectedId: number | null;
  supplierView: boolean;
  canRead: boolean;
  onRead: () => void;
  /** Start a draft BOQ: step 1, priced before any supplier has quoted. */
  onDraft: () => void;
  onOpenAll: () => void;
  onMinimise: () => void;
  wide: boolean;
  onToggleWide: () => void;
}

/** How many rows of a queue its section shows before "Show all". */
const PREVIEW = 3;

/** A count as the documents group thousands: "2 314". */
export function count(n: number | undefined): string {
  return n === undefined ? "…" : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** The left of the commercial screen: every job, arranged by what it waits on.
 *
 * "Needs action" is the work queue -- one collapsible section per queue, each
 * showing its newest three and a way to the rest. "All" and "Done" are flat
 * lists fifty at a time. A search always covers everything, whatever the view.
 * The server decides which queue a job is in and counts them (Fix #23, #24);
 * nothing here holds more of the register than it shows, and the search waits
 * for the typing to pause before it asks (Fix #18). */
export default function JobsRail({
  selectedId,
  supplierView,
  canRead,
  onRead,
  onDraft,
  onOpenAll,
  onMinimise,
  wide,
  onToggleWide,
}: Props) {
  const [text, setText] = useState("");
  const [view, setView] = useState<View>("action");
  const [focus, setFocus] = useState<Queue | null>(null);
  const [order, setOrder] = useState<Order>("newest");
  const [open, setOpen] = useState<Partial<Record<Queue, boolean>>>({});
  const search = useDebounced(text.trim(), 250);

  const everything = useRegisterCounts("");
  const searched = useRegisterCounts(search);
  const counts = searched.data?.counts;
  const eligible = everything.data?.eligible.length ?? 0;
  const sections = sectionsFor(supplierView);

  const flat: { stage: Stage | null; title: string; crumb: boolean } | null =
    search !== ""
      ? { stage: null, title: `${count(searched.data?.counts.all)} matches`, crumb: false }
      : focus !== null
        ? { stage: focus, title: queueLabel(focus, supplierView), crumb: true }
        : view === "action"
          ? null
          : { stage: view === "done" ? "done" : null, title: view === "done" ? "Done" : "All jobs", crumb: false };

  const allOpen = sections.every((section) => open[section.key]);

  return (
    <aside className="split-rail cm-rail" aria-label={supplierView ? "Your quotations" : "Jobs"}>
      <div className="cm-rail-head">
        <div className="cm-rail-title">
          <h2>{supplierView ? "Your quotations" : "Jobs"}</h2>
          <span className="cm-rail-count">{count(everything.data?.counts.all)}</span>
          <div className="cm-rail-tools">
            <button type="button" className="cm-icon" onClick={onMinimise} title="Minimise the list" aria-label="Minimise the list">
              «
            </button>
            <button
              type="button"
              className="cm-icon"
              onClick={onToggleWide}
              title={wide ? "Narrow the list" : "Widen the list"}
              aria-label={wide ? "Narrow the list" : "Widen the list"}
            >
              {wide ? "⇤" : "⇥"}
            </button>
            <button type="button" className="cm-icon" onClick={onOpenAll} title="Open all jobs full screen" aria-label="Open all jobs full screen">
              ⤢
            </button>
          </div>
        </div>
        {canRead ? (
          <button type="button" className="btn btn-primary btn-block" onClick={onRead}>
            + Read a quotation
          </button>
        ) : null}
        {canRead ? (
          <button type="button" className="btn btn-block" onClick={onDraft}>
            + Start a draft BOQ
          </button>
        ) : null}
        {canRead && eligible > 0 ? (
          <p className="cm-rail-note">
            {eligible} inspection{eligible === 1 ? "" : "s"} waiting to be quoted
          </p>
        ) : null}
        <input
          type="search"
          className="search-input cm-search"
          placeholder={supplierView ? "Search ref, site or works order" : "Search ref, site, supplier or works order"}
          aria-label="Search jobs"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <Segmented<View>
          label="Which jobs"
          fill
          value={focus === null ? view : "action"}
          onChange={(next) => {
            setView(next);
            setFocus(null);
          }}
          options={[
            { value: "action", label: <SegmentLabel text="Needs action" n={counts?.action} /> },
            { value: "all", label: <SegmentLabel text="All" n={counts?.all} /> },
            { value: "done", label: <SegmentLabel text="Done" n={counts?.done} /> },
          ]}
        />
      </div>

      <div className="cm-rail-body">
        {searched.isError ? <p className="notice bad cm-rail-error">{searched.error.message}</p> : null}
        {canRead ? <DraftRows search={search} /> : null}
        {flat === null ? (
          <>
            <div className="cm-rail-bar">
              <button
                type="button"
                className="go-link"
                onClick={() =>
                  setOpen(Object.fromEntries(sections.map((section) => [section.key, !allOpen])))
                }
              >
                {allOpen ? "Collapse all" : "Expand all"}
              </button>
            </div>
            <QueueSections
              sections={sections}
              counts={counts}
              open={open}
              onToggle={(key) => setOpen((now) => ({ ...now, [key]: !now[key] }))}
              onShowAll={setFocus}
              search={search}
              selectedId={selectedId}
              supplierView={supplierView}
            />
            <p className="cm-rail-foot">
              Done jobs ({count(counts?.done)}) stay out of this view. Search always covers everything.
            </p>
          </>
        ) : (
          <FlatList
            key={`${flat.stage ?? "all"}-${search}-${order}`}
            stage={flat.stage}
            title={flat.title}
            crumb={flat.crumb ? () => setFocus(null) : null}
            search={search}
            order={order}
            onOrder={setOrder}
            selectedId={selectedId}
            supplierView={supplierView}
          />
        )}
      </div>
    </aside>
  );
}

function SegmentLabel({ text, n }: { text: string; n: number | undefined }) {
  return (
    <>
      {text} <span className="cm-seg-count">{count(n)}</span>
    </>
  );
}

function QueueSections({
  sections,
  counts,
  open,
  onToggle,
  onShowAll,
  search,
  selectedId,
  supplierView,
}: {
  sections: readonly QueueSection[];
  counts: QueueCounts | undefined;
  open: Partial<Record<Queue, boolean>>;
  onToggle: (key: Queue) => void;
  onShowAll: (key: Queue) => void;
  search: string;
  selectedId: number | null;
  supplierView: boolean;
}) {
  if (counts === undefined) {
    return <p className="cm-rail-empty">Loading…</p>;
  }
  const shown = sections.filter((section) => counts[section.key] > 0);
  if (shown.length === 0) {
    return <p className="cm-rail-empty">Nothing needs action.</p>;
  }
  return (
    <>
      {shown.map((section) => (
        <QueueGroup
          key={section.key}
          section={section}
          total={counts[section.key]}
          open={open[section.key] ?? false}
          onToggle={() => onToggle(section.key)}
          onShowAll={() => onShowAll(section.key)}
          search={search}
          selectedId={selectedId}
          supplierView={supplierView}
        />
      ))}
    </>
  );
}

function QueueGroup({
  section,
  total,
  open,
  onToggle,
  onShowAll,
  search,
  selectedId,
  supplierView,
}: {
  section: QueueSection;
  total: number;
  open: boolean;
  onToggle: () => void;
  onShowAll: () => void;
  search: string;
  selectedId: number | null;
  supplierView: boolean;
}) {
  const preview = useQueuePreview(section.key, search, open, PREVIEW);
  const id = `queue-${section.key}`;
  return (
    <section className="cm-queue" aria-labelledby={`${id}-head`}>
      <button
        type="button"
        id={`${id}-head`}
        className="cm-queue-head"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
        data-testid={`queue-${section.key}`}
      >
        <span className="cm-chev" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <span className="cm-queue-label">{section.label}</span>
        <span className={section.mine ? "cm-queue-count mine" : "cm-queue-count"}>{count(total)}</span>
        <span className="cm-queue-hint">{section.hint}</span>
      </button>
      {open ? (
        <div id={id}>
          {preview.isError ? <p className="notice bad cm-rail-error">{preview.error.message}</p> : null}
          {preview.isPending ? <p className="cm-rail-empty">Loading…</p> : null}
          {(preview.data?.rows ?? []).map((row) => (
            <JobRow key={row.id} row={row} selected={row.id === selectedId} supplierView={supplierView} />
          ))}
          {total > PREVIEW ? (
            <button type="button" className="cm-more" onClick={onShowAll}>
              Show all {count(total)} →
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function FlatList({
  stage,
  title,
  crumb,
  search,
  order,
  onOrder,
  selectedId,
  supplierView,
}: {
  stage: Stage | null;
  title: string;
  crumb: (() => void) | null;
  search: string;
  order: Order;
  onOrder: (order: Order) => void;
  selectedId: number | null;
  supplierView: boolean;
}) {
  const list = useRegisterList(stage, search, order);
  const rows: QuotationRow[] = list.data?.pages.flatMap((page) => page.rows) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;
  const left = total - rows.length;
  return (
    <>
      <div className="cm-rail-bar cm-flat-bar">
        {crumb !== null ? (
          <>
            <button type="button" className="go-link" onClick={crumb}>
              ← Needs action
            </button>
            <span aria-hidden="true">/</span>
          </>
        ) : null}
        <span className="cm-flat-title">{title}</span>
        {rows.length > 0 ? (
          <span>
            1–{count(rows.length)} of {count(total)}
          </span>
        ) : null}
        <select
          className="cm-sort"
          aria-label="Order"
          value={order}
          onChange={(event) => onOrder(event.target.value === "oldest" ? "oldest" : "newest")}
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </div>
      {list.isError ? <p className="notice bad cm-rail-error">{list.error.message}</p> : null}
      {list.isPending ? <p className="cm-rail-empty">Loading…</p> : null}
      {list.isSuccess && rows.length === 0 ? (
        <p className="cm-rail-empty">Nothing matches. Clear the search or pick another filter.</p>
      ) : null}
      {rows.map((row) => (
        <JobRow key={row.id} row={row} selected={row.id === selectedId} supplierView={supplierView} />
      ))}
      {list.hasNextPage ? (
        <button
          type="button"
          className="btn btn-secondary cm-load-more"
          disabled={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          {list.isFetchingNextPage ? "Loading…" : `Show ${Math.min(50, left)} more`}
        </button>
      ) : null}
    </>
  );
}
